import { useState, useEffect, useCallback } from "react";
import {
  FiSearch, FiX, FiCheck, FiPlus, FiMinus, FiTrash2, FiArrowLeft,
  FiAlertTriangle, FiArrowRight, FiChevronDown, FiChevronRight, FiRepeat,
} from "react-icons/fi";
import { apiGet, apiPost } from "../api";
import { Btn, Pill, Card, THead, TD } from "./KioskUI";

const numOnly = (s) => (String(s).match(/\d/g) || []).join("");
const fmtDate = (s) => new Date(s.replace(" ", "T")).toLocaleString("en-US", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });

const lbl      = { display: "block", fontSize: 11.5, fontWeight: 700, color: "var(--sub)", letterSpacing: ".02em", marginBottom: 6 };
const fieldBox = { display: "flex", alignItems: "center", gap: 8, border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "9px 12px", background: "var(--surface)" };
const sqbtn    = { width: 30, height: 30, borderRadius: 8, border: "1px solid var(--line)", background: "var(--surface)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };

function stockStatus(p) {
  if (p.quantity === 0) return "out";
  if (p.quantity <= p.low_stock_threshold) return "low";
  return "ok";
}

/* ─── location picker (inline dropdown) ─── */
function LocationPicker({ label, locations, value, onChange, exclude }) {
  const [open, setOpen] = useState(false);
  const options = locations.filter(l => l.id !== exclude);
  return (
    <div>
      <label style={lbl}>{label}</label>
      <div style={{ position: "relative" }}>
        <button type="button" onClick={() => setOpen(v => !v)}
          style={{ ...fieldBox, width: "100%", justifyContent: "space-between", cursor: "pointer" }}>
          <span style={{ fontFamily: "var(--font-body)", fontSize: 13.5, fontWeight: 700, color: value ? "var(--ink)" : "var(--n-400)" }}>
            {value?.name || "Choose…"}
          </span>
          <FiChevronDown size={14} color="var(--n-400)" />
        </button>
        {open && (
          <>
            <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 30 }} />
            <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-md)", boxShadow: "var(--shadow-md)", zIndex: 40, overflow: "hidden" }}>
              {options.length === 0 && (
                <div style={{ padding: "12px 14px", fontSize: 13, color: "var(--n-400)" }}>No other location</div>
              )}
              {options.map(l => (
                <button key={l.id} type="button" onClick={() => { onChange(l); setOpen(false); }}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", border: "none", background: value?.id === l.id ? "var(--blue-50)" : "transparent", cursor: "pointer", fontFamily: "var(--font-body)", fontSize: 13.5, fontWeight: value?.id === l.id ? 700 : 500, color: "var(--ink)" }}
                  onMouseEnter={e => { if (value?.id !== l.id) e.currentTarget.style.background = "var(--faint)"; }}
                  onMouseLeave={e => { if (value?.id !== l.id) e.currentTarget.style.background = "transparent"; }}
                >
                  {l.name}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── History ─── */
function TransferHistory({ token }) {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    apiGet("/api/inventory/transfers", token)
      .then(r => r.json())
      .then(d => setTransfers(Array.isArray(d) ? d : []))
      .catch(() => setTransfers([]))
      .finally(() => setLoading(false));
  }, [token]);

  if (selectedId) {
    return <TransferDetail transferId={selectedId} token={token} onBack={() => setSelectedId(null)} />;
  }

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300 }}>
        <span style={{ color: "var(--sub)", fontSize: 14 }}>Loading…</span>
      </div>
    );
  }

  if (transfers.length === 0) {
    return (
      <div style={{ textAlign: "center", color: "var(--n-400)", fontSize: 14, padding: "48px 0" }}>
        No transfers yet.
      </div>
    );
  }

  return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-lg)", boxShadow: "var(--shadow-sm)", overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <THead cols={[{ t: "Date" }, { t: "From" }, { t: "" }, { t: "To" }, { t: "Items", r: 1 }, { t: "Employee" }, { t: "Note" }, { t: "", r: 1, w: 30 }]} />
        <tbody>
          {transfers.map(t => (
            <tr key={t.transfer_id} style={{ cursor: "pointer" }}
              onClick={() => setSelectedId(t.transfer_id)}
              onMouseEnter={e => e.currentTarget.style.background = "var(--faint)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <TD mut>{fmtDate(t.date)}</TD>
              <TD b>{t.from_location}</TD>
              <td style={{ padding: "14px 8px", borderTop: "1px solid var(--hair)" }}><FiArrowRight size={14} color="var(--n-400)" /></td>
              <TD b>{t.to_location}</TD>
              <TD r>{t.items_count}</TD>
              <TD mut>{t.employee_name || "—"}</TD>
              <TD mut>{t.note || "—"}</TD>
              <td style={{ padding: "14px 16px", borderTop: "1px solid var(--hair)", textAlign: "right" }}>
                <FiChevronRight size={17} color="var(--n-300)" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── History detail ─── */
function TransferDetail({ transferId, token, onBack }) {
  const [detail, setDetail] = useState(null);
  const [error, setError]   = useState("");

  useEffect(() => {
    apiGet(`/api/inventory/transfers/${transferId}`, token)
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setDetail(d); })
      .catch(() => setError("Cannot load transfer."));
  }, [transferId, token]);

  if (error) {
    return <div style={{ textAlign: "center", padding: "48px 0", color: "var(--danger)" }}>{error}</div>;
  }
  if (!detail) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 240 }}>
        <span style={{ color: "var(--sub)", fontSize: 14 }}>Loading…</span>
      </div>
    );
  }

  const totalUnits = detail.items.reduce((s, i) => s + i.quantity, 0);

  return (
    <div style={{ maxWidth: 780, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
        <button onClick={onBack} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 8, cursor: "pointer", display: "flex" }}>
          <FiArrowLeft size={18} color="var(--sub)" />
        </button>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 11, minWidth: 0 }}>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 800, letterSpacing: "-.02em", margin: 0, color: "var(--ink)", display: "flex", alignItems: "center", gap: 10 }}>
            {detail.from_location} <FiArrowRight size={17} color="var(--n-400)" /> {detail.to_location}
          </h1>
          <Pill tone="info">Transfer #{detail.transfer_id}</Pill>
        </div>
      </div>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "18px 26px", borderBottom: "1px solid var(--hair)", display: "flex", gap: 48, flexWrap: "wrap" }}>
          <MetaField label="Date" value={fmtDate(detail.date)} />
          <MetaField label="Moved by" value={detail.employee} />
          <MetaField label="Total units" value={totalUnits} />
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <THead cols={[{ t: "Product" }, { t: "SKU" }, { t: "Quantity", r: 1 }]} />
          <tbody>
            {detail.items.map((item, i) => (
              <tr key={i}>
                <TD b>{item.product_name}</TD>
                <TD mut>{item.sku || "—"}</TD>
                <TD r>{item.quantity}</TD>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {detail.note && (
        <div style={{ marginTop: 16, padding: "12px 16px", background: "var(--faint)", borderRadius: "var(--r-md)", fontSize: 13, color: "var(--sub)" }}>
          Note: {detail.note}
        </div>
      )}
    </div>
  );
}

function MetaField({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--n-400)" }}>{label}</div>
      <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink)", marginTop: 4 }}>{value}</div>
    </div>
  );
}

/* ─── Success modal ─── */
function TransferSuccess({ fromName, toName, itemCount, go, onNew }) {
  return (
    <div onClick={() => go("overview")} style={{ position: "fixed", inset: 0, background: "rgba(19,24,32,.42)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: "var(--r-xl)", boxShadow: "0 24px 70px rgba(19,24,32,.3)", width: 400, padding: "30px 28px 24px", textAlign: "center" }}>
        <div style={{ width: 62, height: 62, borderRadius: 99, background: "var(--success-bg)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
          <FiCheck size={30} color="var(--success)" strokeWidth={2.4} />
        </div>
        <div style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 800, color: "var(--ink)" }}>Transfer complete</div>
        <div style={{ fontSize: 14, color: "var(--sub)", marginTop: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {fromName} <FiArrowRight size={13} /> {toName}
        </div>
        <div style={{ background: "var(--faint)", border: "1px solid var(--line)", borderRadius: "var(--r-md)", padding: "14px 18px", margin: "20px 0", fontSize: 13.5 }}>
          {itemCount} item{itemCount !== 1 ? "s" : ""} moved · stock updated on both sides
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Btn kind="ghost" onClick={onNew} style={{ flex: 1, justifyContent: "center" }}>New Transfer</Btn>
          <Btn kind="pri" onClick={() => go("overview")} style={{ flex: 1, justifyContent: "center" }}>Done</Btn>
        </div>
      </div>
    </div>
  );
}

/* ─── Main ─── */
export default function Transfer({ token, go, locations, currentLocation }) {
  const [tab, setTab] = useState("new"); // "new" | "history"
  const [fromLocation, setFromLocation] = useState(currentLocation || null);
  const [toLocation, setToLocation]     = useState(null);
  const [products, setProducts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [note, setNote]           = useState("");
  const [lines, setLines]         = useState([]);
  const [prodQuery, setProdQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState("");
  const [success, setSuccess]       = useState(null);

  const fetchProducts = useCallback(async () => {
    if (!fromLocation?.id) { setProducts([]); setLoading(false); return; }
    setLoading(true);
    try {
      const r = await apiGet(`/api/inventory/products/by-location?location_id=${fromLocation.id}`, token);
      const d = await r.json();
      setProducts(Array.isArray(d) ? d : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token, fromLocation?.id]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { setLines([]); }, [fromLocation?.id]);

  const addProduct = useCallback((p) => {
    setLines(ls => ls.find(l => l.productId === p.id)
      ? ls.map(l => l.productId === p.id ? { ...l, qty: Math.min(p.quantity, l.qty + 1) } : l)
      : [...ls, { productId: p.id, qty: 1 }]);
    setProdQuery(""); setError("");
  }, []);

  const bumpQty  = (id, d, max) => setLines(ls => ls.map(l => l.productId === id ? { ...l, qty: Math.min(max, Math.max(1, l.qty + d)) } : l));
  const setQty   = (id, v, max) => { const n = Math.min(max, Math.max(1, parseInt(numOnly(v) || "1", 10) || 1)); setLines(ls => ls.map(l => l.productId === id ? { ...l, qty: n } : l)); };
  const removeLn = (id)    => setLines(ls => ls.filter(l => l.productId !== id));

  const handleTransfer = async () => {
    if (!fromLocation || !toLocation) return setError("Choose both a from and a to location.");
    if (fromLocation.id === toLocation.id) return setError("From and to locations must be different.");
    if (lines.length === 0) return setError("Add at least one product to transfer.");
    setSubmitting(true); setError("");
    try {
      const body = {
        employee_token: token,
        from_location_id: fromLocation.id,
        to_location_id: toLocation.id,
        note,
        products: lines.map(l => ({ product_id: l.productId, quantity: l.qty })),
      };
      const res = await apiPost("/api/inventory/products/transfer", body, token);
      if (res.ok) {
        setSuccess({ fromName: fromLocation.name, toName: toLocation.name, itemCount: lines.length });
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.error || "Failed to transfer stock.");
      }
    } catch { setError("Cannot connect to server."); }
    finally { setSubmitting(false); }
  };

  const resetTransfer = async () => {
    setLines([]); setNote(""); setSuccess(null); setError("");
    await fetchProducts();
  };

  const prodMatches = prodQuery.trim()
    ? products.filter(p => p.name.toLowerCase().includes(prodQuery.toLowerCase()) || (p.sku || "").toLowerCase().includes(prodQuery.toLowerCase()))
    : [];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 27, fontWeight: 800, letterSpacing: "-.025em", color: "var(--ink)", marginBottom: 4 }}>
            Transfer Stock <span style={{ fontFamily: "var(--font-mm)", fontSize: 16, fontWeight: 500, color: "var(--n-400)" }}>ပြောင်းရွှေ့</span>
          </div>
          <div style={{ fontSize: 14, color: "var(--sub)" }}>
            Move stock that's already owned by the business between locations — the total doesn't change.
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, background: "var(--faint)", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: 4, flexShrink: 0 }}>
          {[["new", "New Transfer"], ["history", "History"]].map(([id, t]) => (
            <button key={id} onClick={() => setTab(id)}
              style={{ padding: "8px 16px", borderRadius: 7, cursor: "pointer", border: "none", background: tab === id ? "var(--surface)" : "transparent", boxShadow: tab === id ? "var(--shadow-sm)" : "none", color: tab === id ? "var(--ink)" : "var(--sub)", fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 13.5 }}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {tab === "history" ? (
        <TransferHistory token={token} />
      ) : (
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>

      {/* ── LEFT: product search + grid ── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!fromLocation ? (
          <div style={{ textAlign: "center", color: "var(--n-400)", fontSize: 14, padding: "48px 0" }}>
            Choose a "From" location to see what's available there.
          </div>
        ) : (
          <>
            {/* product search */}
            <div style={{ position: "relative", marginBottom: 14 }}>
              <div style={{ ...fieldBox, padding: "11px 14px" }}>
                <FiSearch size={18} color="var(--n-400)" style={{ flexShrink: 0 }} />
                <input value={prodQuery} onChange={e => setProdQuery(e.target.value)} placeholder="Search product name or SKU…"
                  style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 14.5, color: "var(--ink)", width: "100%" }} />
                {prodQuery && <button onClick={() => setProdQuery("")} style={{ border: "none", background: "transparent", cursor: "pointer" }}><FiX size={15} color="var(--n-400)" /></button>}
              </div>
              {prodMatches.length > 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-md)", boxShadow: "var(--shadow-md)", zIndex: 20, overflow: "hidden", maxHeight: 300, overflowY: "auto" }}>
                  {prodMatches.slice(0, 7).map(p => (
                    <button key={p.id} disabled={p.quantity === 0} onClick={() => addProduct(p)}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", border: "none", borderBottom: "1px solid var(--hair)", background: "transparent", cursor: p.quantity === 0 ? "not-allowed" : "pointer", textAlign: "left", opacity: p.quantity === 0 ? .5 : 1 }}
                      onMouseEnter={e => { if (p.quantity > 0) e.currentTarget.style.background = "var(--faint)"; }}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>{p.name}</div>
                        <div style={{ fontSize: 12, color: "var(--sub)", marginTop: 2 }}>{p.sku} · {p.quantity} at {fromLocation.name}</div>
                      </div>
                      <FiPlus size={17} color="var(--primary)" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* product grid */}
            {loading ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
                <span style={{ color: "var(--sub)", fontSize: 14 }}>Loading…</span>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12 }}>
                {products.map(p => {
                  const inList = lines.find(l => l.productId === p.id);
                  const st = stockStatus(p);
                  const empty = p.quantity === 0;
                  return (
                    <button key={p.id} disabled={empty} onClick={() => addProduct(p)}
                      style={{ display: "flex", flexDirection: "column", textAlign: "left", background: "var(--surface)", border: `1px solid ${inList ? "var(--primary)" : "var(--line)"}`, borderRadius: "var(--r-md)", padding: "14px 16px", cursor: empty ? "not-allowed" : "pointer", boxShadow: "var(--shadow-sm)", opacity: empty ? .55 : 1 }}
                      onMouseEnter={e => { if (!inList && !empty) e.currentTarget.style.borderColor = "var(--blue-100)"; }}
                      onMouseLeave={e => { if (!inList) e.currentTarget.style.borderColor = "var(--line)"; }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                        <div style={{ fontFamily: "var(--font-head)", fontSize: 14, fontWeight: 700, color: "var(--ink)", lineHeight: 1.25, minHeight: 34, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.name}</div>
                        <span style={{ width: 23, height: 23, borderRadius: 7, background: inList ? "var(--primary)" : "var(--blue-50)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {inList ? <FiCheck size={14} color="#fff" /> : <FiPlus size={14} color="var(--primary)" />}
                        </span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 7 }}>
                        <Pill tone={st}>{p.quantity}</Pill>
                        <span style={{ fontSize: 11.5, color: "var(--sub)" }}>at {fromLocation.name}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── RIGHT: transfer panel ── */}
      <div style={{ width: 400, flexShrink: 0, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-lg)", boxShadow: "var(--shadow-sm)", display: "flex", flexDirection: "column", position: "sticky", top: 0, maxHeight: "calc(100vh - 84px)", overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--hair)", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 17, fontWeight: 800, color: "var(--ink)", display: "flex", alignItems: "center", gap: 8 }}>
            <FiRepeat size={16} color="var(--primary)" /> Transfer
          </div>
          <span style={{ fontSize: 12.5, color: "var(--sub)" }}>Draft</span>
        </div>

        <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          {/* from / to */}
          <div style={{ padding: "14px 20px 6px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, alignItems: "end" }}>
            <LocationPicker label="From" locations={locations} value={fromLocation} onChange={setFromLocation} exclude={toLocation?.id} />
            <LocationPicker label="To" locations={locations} value={toLocation} onChange={setToLocation} exclude={fromLocation?.id} />
          </div>

          {/* note */}
          <div style={{ padding: "10px 20px 6px" }}>
            <label style={lbl}>Note (optional)</label>
            <div style={fieldBox}>
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. weekly restock to market"
                style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 13.5, color: "var(--ink)", width: "100%" }} />
            </div>
          </div>

          {/* line items */}
          <div style={{ padding: "10px 16px 6px" }}>
            <label style={{ ...lbl, paddingLeft: 4 }}>Items to move</label>
            {lines.length === 0 && (
              <div style={{ textAlign: "center", color: "var(--n-400)", fontSize: 13, padding: "26px 0" }}>
                No items yet — search or tap a product
              </div>
            )}
            {lines.map(l => {
              const p = products.find(x => x.id === l.productId) || {};
              const max = p.quantity ?? l.qty;
              return (
                <div key={l.productId} style={{ padding: "11px 8px", borderBottom: "1px solid var(--hair)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: "var(--sub)", marginTop: 1 }}>{p.sku} · {max} available</div>
                    </div>
                    <button onClick={() => removeLn(l.productId)} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 4 }}>
                      <FiTrash2 size={14} color="var(--n-400)" />
                    </button>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <button onClick={() => bumpQty(l.productId, -1, max)} style={sqbtn}><FiMinus size={13} color="var(--n-600)" /></button>
                      <input value={l.qty} onChange={e => setQty(l.productId, e.target.value, max)}
                        style={{ width: 46, height: 30, textAlign: "center", border: "1px solid var(--line)", borderRadius: 7, fontFamily: "var(--font-head)", fontSize: 13.5, fontWeight: 700, color: "var(--ink)", fontVariantNumeric: "tabular-nums", outline: "none" }} />
                      <button onClick={() => bumpQty(l.productId, 1, max)} style={sqbtn}><FiPlus size={13} color="var(--n-600)" /></button>
                    </div>
                    <span style={{ fontSize: 12, color: "var(--n-400)" }}>of {max} pcs</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* footer */}
        <div style={{ padding: "14px 20px 18px", borderTop: "1px solid var(--hair)", background: "var(--faint)", borderRadius: "0 0 var(--r-lg) var(--r-lg)", flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
            <span style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>Total units</span>
            <span style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 800, color: "var(--ink)", fontVariantNumeric: "tabular-nums" }}>
              {lines.reduce((s, l) => s + l.qty, 0)}
            </span>
          </div>
          {error && (
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 8, color: "var(--danger)", fontSize: 12.5, fontWeight: 600 }}>
              <FiAlertTriangle size={14} /> {error}
            </div>
          )}
          <Btn kind="pri" onClick={handleTransfer} disabled={submitting || lines.length === 0}
            style={{ width: "100%", justifyContent: "center", height: 54, marginTop: 13, fontSize: 16 }}>
            <FiRepeat size={18} /> {submitting ? "Moving…" : "Move Stock"}
          </Btn>
        </div>
      </div>

      {success && (
        <TransferSuccess
          {...success}
          go={go}
          onNew={resetTransfer}
        />
      )}
      </div>
      )}
    </div>
  );
}
